/**
 * Subscription State Hook
 *
 * Provides subscription tier, features, token usage, and grace period state.
 * Reads from AuthContext organization data and API response headers.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { subscriptionEvents } from '@/services/api'

// Tier ordering for comparison
const TIER_ORDER: Record<string, number> = {
  trial: 0,
  starter: 1,
  professional: 2,
  enterprise: 3,
  unlimited: 4,
}

interface TokenUsage {
  used: number
  limit: number
  remaining: number
}

interface SubscriptionState {
  tier: string | null
  features: string[]
  isGracePeriod: boolean
  gracePeriodEnds: string | null
  graceDaysRemaining: number | null
  tokenUsage: TokenUsage | null
  isLoading: boolean
}

export function useSubscription() {
  const { organization } = useAuth()
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null)
  const [isGracePeriod, setIsGracePeriod] = useState(false)
  const [gracePeriodEnds, setGracePeriodEnds] = useState<string | null>(null)
  const [graceDaysRemaining, setGraceDaysRemaining] = useState<number | null>(null)

  // Listen for subscription events from API client
  useEffect(() => {
    const handleTokenUpdate = (usage: any) => {
      setTokenUsage(usage)
    }

    const handleGracePeriod = (data: any) => {
      setIsGracePeriod(true)
      setGracePeriodEnds(data.ends)
      setGraceDaysRemaining(data.daysRemaining)
    }

    subscriptionEvents.on('tokenUpdate', handleTokenUpdate)
    subscriptionEvents.on('gracePeriod', handleGracePeriod)

    return () => {
      subscriptionEvents.off('tokenUpdate', handleTokenUpdate)
      subscriptionEvents.off('gracePeriod', handleGracePeriod)
    }
  }, [])

  const tier = organization?.tier || null
  const features = useMemo(() => organization?.features || [], [organization?.features])

  const isFeatureEnabled = useCallback(
    (feature: string): boolean => {
      if (!features.length) return true // No features list = all enabled
      return features.includes(feature)
    },
    [features]
  )

  const isTierAtLeast = useCallback(
    (requiredTier: string): boolean => {
      if (!tier) return false
      const currentOrder = TIER_ORDER[tier] ?? -1
      const requiredOrder = TIER_ORDER[requiredTier] ?? 999
      return currentOrder >= requiredOrder
    },
    [tier]
  )

  const refreshUsage = useCallback(async () => {
    // Token usage is automatically updated from API response headers
    // This method can be used to force a refresh by making any API call
  }, [])

  const state: SubscriptionState = {
    tier,
    features,
    isGracePeriod,
    gracePeriodEnds,
    graceDaysRemaining,
    tokenUsage,
    isLoading: false,
  }

  return {
    ...state,
    isFeatureEnabled,
    isTierAtLeast,
    refreshUsage,
  }
}
